import React, { useEffect, useMemo, useRef, useState } from "react";

import { ChronoUnit, ZonedDateTime } from "@js-joda/core";
import { useGetObjectDatasetDataInRange } from "api/api-hooks/object-dataset-hooks";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { FileInfo } from "components/dashboard/product-template-table/utils/interfaces";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { useDisplayTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { CalendarDateTemplateEvent } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { getTokenParam } from "services/HttpService";
import { T } from "services/lang/LanguageService";
import { DATE_FORMATTER_HOURS_MINUTES_ONLY, DEFAULT_CALENDAR_FORMAT } from "utils/constants";
import { fmtDatetime, toInstant, toNativeDate } from "utils/time-utils";
import { getBlobUrl } from "utils/utils";
import { ObjectDatasetConfig, ObjectDatasetDataDto, ObjectDatasetWithDaysDto } from "../object-dataset-types";
import { getFileUrl } from "../utils/utils";

type ObjectDatasetCommonProps = ObjectDatasetConfig & {
  data: ObjectDatasetWithDaysDto | undefined;
};

const ObjectDatasetCommon: React.FC<ObjectDatasetCommonProps> = props => {
  const { data } = props;

  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [objectData, setObjectData] = useState<ObjectDatasetDataDto | null>(null);
  const [content, setContent] = useState<FileInfo | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null | undefined>(null);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const company = useCurrentCompanyContext();
  const displayTz = useDisplayTimezone();

  const { data: objectDatasetData, isLoading } = useGetObjectDatasetDataInRange(
    {
      identifier: props.identifier ?? "",
      companyRef: company?.companyReference ?? "",
      startDate: toInstant(selectedDate!),
      endDate: toInstant(selectedDate!)?.plus(1, ChronoUnit.DAYS),
    },
    {
      enabled: !!company?.companyReference && !!props.identifier && !!selectedDate,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (data && data.distinctDays?.length > 0) {
      const latest = data.distinctDays.at(-1);
      setSelectedDate(toNativeDate(latest!));
    }
  }, [data]);

  useEffect(() => {
    if (objectDatasetData) {
      setObjectData(objectDatasetData.at(0) ?? null);
    }
  }, [objectDatasetData]);

  useEffect(() => {
    if (objectData) {
      const foundFile = objectData.fileInfos.find(
        file => props.initialFileKey && file.key === props.initialFileKey
      );
      setContent(foundFile ?? objectData.fileInfos[0]);
    }
  }, [objectData, props.initialFileKey]);

  const fileUrl = useMemo(
    () => getFileUrl(company?.companyReference, props.identifier, objectData?.timestamp, content?.key ?? ""),
    [company?.companyReference, content, objectData?.timestamp, props.identifier]
  );

  useEffect(() => {
    if (!content) return;
    const token = getTokenParam();
    if (!token) {
      setImageBlobUrl(fileUrl);
      return;
    }

    const loadImage = async () => {
      const blobUrl = await getBlobUrl(
        `/object-dataset/${company?.companyReference}/${props.identifier}/${objectData?.timestamp}/${content}`
      );
      setImageBlobUrl(blobUrl);
    };

    loadImage();
  }, [company?.companyReference, content, fileUrl, objectData?.timestamp, props.identifier]);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      const iframeDoc = iframe.contentWindow.document;
      const height = Array.from(iframeDoc.body.children).reduce((acc, child) => {
        return acc + child.scrollHeight;
      }, 0);
      iframe.style.height = `${height + 40}px`;
    }
    setIsLoadingFile(false);
  };

  const handleOnDateChange = (date: Date | null | undefined) => {
    setSelectedDate(date);
  };

  const dateTemplate = (date: CalendarDateTemplateEvent) => {
    const objectDates = data?.distinctDays.map(d => {
      const zdt = ZonedDateTime.parse(d).withZoneSameInstant(displayTz);
      return {
        day: zdt.dayOfMonth(),
        month: zdt.monthValue(),
        year: zdt.year(),
      };
    });

    const match = objectDates?.some(
      od => od.day === date.day && od.month === date.month + 1 && od.year === date.year
    );

    if (match) {
      return <strong className="text-primary-500 day-bg">{date.day}</strong>;
    }

    return date.day;
  };

  return (
    <div className="flex flex-wrap gap-3">
      <div className="flex gap-2 w-full flex-wrap">
        <CalendarWrapper
          value={selectedDate}
          onChange={e => handleOnDateChange(e)}
          dateFormat={DEFAULT_CALENDAR_FORMAT}
          dateTemplate={dateTemplate}
          className="w-10rem"
          panelClassName="day-picker-panel"
          readOnlyInput
          disabled={isLoading}
          showTime={false}
        />
        <Dropdown
          value={objectData}
          options={objectDatasetData}
          onChange={e => setObjectData(e.value)}
          loading={isLoading}
          optionLabel="timestamp"
          placeholder={T("word.not-found")}
          valueTemplate={
            !!objectData &&
            (opt => opt && fmtDatetime(opt.timestamp, displayTz, DATE_FORMATTER_HOURS_MINUTES_ONLY))
          }
          itemTemplate={opt =>
            opt && fmtDatetime(opt.timestamp, displayTz, DATE_FORMATTER_HOURS_MINUTES_ONLY)
          }
        />
        {objectData && objectData.fileInfos.length > 1 && (
          <Dropdown
            options={objectData.fileInfos}
            value={content}
            optionLabel="fileName"
            onChange={e => setContent(e.value)}
          />
        )}
        <Button
          type="button"
          onClick={() => window.open(`${fileUrl}?dl=true`, "_blank")}
          icon="pi pi-download"
          label={T("action.download")}
          disabled={!objectData}
          className="component-editor-button icon-green"
        />
      </div>
      {isLoadingFile && (
        <div className="w-full align-self-center">
          <ProgressSpinner style={{ width: "10rem" }} />
        </div>
      )}
      {!objectData && (
        <div className="text-sm text-gray-500 align-self-center my-5">{T("message.no-models")}</div>
      )}
      {content && data?.type === "IMAGE" && (
        <img
          src={imageBlobUrl ?? fileUrl}
          alt="model-view"
          loading="lazy"
          onLoad={() => setIsLoadingFile(false)}
          style={{ objectFit: "contain", width: "100%", height: "auto" }}
        />
      )}
      {content && data?.type !== "IMAGE" && (
        <iframe
          ref={iframeRef}
          loading="lazy"
          src={imageBlobUrl ?? fileUrl}
          title="model-view"
          onLoad={handleIframeLoad}
          style={{ maxHeight: "200vh" }}
          className="flex-1 border-none"
        />
      )}
    </div>
  );
};

export default ObjectDatasetCommon;
